import React, {PropsWithChildren, useCallback, useContext, useEffect, useRef, useState} from 'react';
import {IElementLineV2} from '../../../../incase-domain/elements/shapes/lineV2';
import useElementActions from '../../../../utils/hooks/slideUpdates/useElementActions';
import {IElementArrowV2} from '../../../../incase-domain/elements/shapes/arrowV2';
import {usePresScaleContext} from '../../../../context/PresScaleContext';
import {SlideRefContext} from '../../../../context/SlideRefContext';

interface LineElementWrapperProps {
    isActive?: boolean;
    onOptions?: (id: string, silent?: boolean) => void;
    element: IElementLineV2 | IElementArrowV2;
    elementIndex: number;
}

type Point = {x: number; y: number};
type DragType = 'start' | 'end' | 'line';

type DragSession = {
    type: DragType;
    grabDx: number;
    grabDy: number;
    vector: Point;
};

const SNAP_THRESHOLD = 10;

function clientToWorkspace(
    clientX: number,
    clientY: number,
    slideEl: HTMLElement | null,
    workspaceSize: {width: number; height: number}
): Point {
    if (!slideEl) return {x: 0, y: 0};
    const rect = slideEl.getBoundingClientRect();
    const visualWidth = rect.width || 1;
    const visualHeight = rect.height || 1;
    return {
        x: ((clientX - rect.left) / visualWidth) * workspaceSize.width,
        y: ((clientY - rect.top) / visualHeight) * workspaceSize.height,
    };
}

function clampPoint(point: Point, workspaceSize: {width: number; height: number}): Point {
    return {
        x: Math.min(workspaceSize.width, Math.max(0, point.x)),
        y: Math.min(workspaceSize.height, Math.max(0, point.y)),
    };
}

function snapToAxis(point: Point, other: Point): Point {
    const next = {...point};
    if (Math.abs(next.x - other.x) < SNAP_THRESHOLD) next.x = other.x;
    if (Math.abs(next.y - other.y) < SNAP_THRESHOLD) next.y = other.y;
    return next;
}

function clampTranslatedLine(start: Point, vector: Point, workspaceSize: {width: number; height: number}): {start: Point; end: Point} {
    let x1 = start.x;
    let y1 = start.y;
    let x2 = x1 + vector.x;
    let y2 = y1 + vector.y;

    if (x1 < 0) {
        x1 = 0;
        x2 = vector.x;
    }
    if (x1 > workspaceSize.width) {
        x1 = workspaceSize.width;
        x2 = x1 + vector.x;
    }
    if (y1 < 0) {
        y1 = 0;
        y2 = vector.y;
    }
    if (y1 > workspaceSize.height) {
        y1 = workspaceSize.height;
        y2 = y1 + vector.y;
    }
    if (x2 < 0) {
        x2 = 0;
        x1 = x2 - vector.x;
    }
    if (x2 > workspaceSize.width) {
        x2 = workspaceSize.width;
        x1 = x2 - vector.x;
    }
    if (y2 < 0) {
        y2 = 0;
        y1 = y2 - vector.y;
    }
    if (y2 > workspaceSize.height) {
        y2 = workspaceSize.height;
        y1 = y2 - vector.y;
    }

    return {start: {x: x1, y: y1}, end: {x: x2, y: y2}};
}

function LineElementWrapper({
    element,
    elementIndex,
    isActive,
    onOptions,
    children,
}: PropsWithChildren<LineElementWrapperProps>): JSX.Element {
    const {updateElement} = useElementActions();
    const {slideRef} = useContext(SlideRefContext);
    const {workspaceSize, transformPointFromFraction, transformPointToFraction} = usePresScaleContext();
    const draggingRef = useRef<DragSession | null>(null);
    const startPointRef = useRef<Point>(transformPointFromFraction(element.startPoint));
    const endPointRef = useRef<Point>(transformPointFromFraction(element.endPoint));
    const [startPoint, setStartPoint] = useState<Point>(startPointRef.current);
    const [endPoint, setEndPoint] = useState<Point>(endPointRef.current);

    startPointRef.current = startPoint;
    endPointRef.current = endPoint;

    useEffect(() => {
        if (draggingRef.current) return;
        const nextStart = transformPointFromFraction(element.startPoint);
        const nextEnd = transformPointFromFraction(element.endPoint);
        startPointRef.current = nextStart;
        endPointRef.current = nextEnd;
        setStartPoint(nextStart);
        setEndPoint(nextEnd);
    }, [element.endPoint, element.startPoint, transformPointFromFraction]);

    const toWorkspace = useCallback(
        (clientX: number, clientY: number): Point => clientToWorkspace(clientX, clientY, slideRef.current, workspaceSize),
        [slideRef, workspaceSize]
    );

    useEffect(() => {
        const onMouseMove = (e: MouseEvent): void => {
            const drag = draggingRef.current;
            if (!drag) return;

            const pointer = toWorkspace(e.clientX, e.clientY);

            if (drag.type === 'line') {
                const translated = clampTranslatedLine(
                    {x: pointer.x - drag.grabDx, y: pointer.y - drag.grabDy},
                    drag.vector,
                    workspaceSize
                );
                startPointRef.current = translated.start;
                endPointRef.current = translated.end;
                setStartPoint(translated.start);
                setEndPoint(translated.end);
                return;
            }

            const unclamped = {x: pointer.x - drag.grabDx, y: pointer.y - drag.grabDy};
            const next = clampPoint(unclamped, workspaceSize);

            if (drag.type === 'start') {
                const snapped = snapToAxis(next, endPointRef.current);
                startPointRef.current = snapped;
                setStartPoint(snapped);
            } else {
                const snapped = snapToAxis(next, startPointRef.current);
                endPointRef.current = snapped;
                setEndPoint(snapped);
            }
        };

        const onMouseUp = (): void => {
            if (!draggingRef.current) return;
            draggingRef.current = null;
            updateElement(elementIndex, {
                ...element,
                startPoint: transformPointToFraction(startPointRef.current),
                endPoint: transformPointToFraction(endPointRef.current),
            });
        };

        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
        return () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };
    }, [element, elementIndex, toWorkspace, transformPointToFraction, updateElement, workspaceSize]);

    const beginDrag = (type: DragType, e: React.MouseEvent<HTMLElement>): void => {
        e.stopPropagation();
        e.preventDefault();
        onOptions?.(element.id);

        const pointer = toWorkspace(e.clientX, e.clientY);
        const start = startPointRef.current;
        const end = endPointRef.current;
        const origin = type === 'end' ? end : start;

        draggingRef.current = {
            type,
            grabDx: pointer.x - origin.x,
            grabDy: pointer.y - origin.y,
            vector: {x: end.x - start.x, y: end.y - start.y},
        };
    };

    return (
        <div className={`c-lineElementWrapper ${isActive ? 'is-active' : ''}`} onMouseDown={(e) => beginDrag('line', e)}>
            {React.Children.map(children, (child) =>
                React.isValidElement(child)
                    ? React.cloneElement(child as React.ReactElement<{startPointPx?: Point; endPointPx?: Point}>, {
                          startPointPx: startPoint,
                          endPointPx: endPoint,
                      })
                    : child
            )}
            {isActive && (
                <div className="c-lineElementWrapper__handlers">
                    <div
                        id="startPoint"
                        className="c-lineElementWrapper__handlers_circle"
                        style={{top: startPoint.y, left: startPoint.x}}
                        onMouseDown={(e) => beginDrag('start', e)}
                    />
                    <div
                        id="endPoint"
                        className="c-lineElementWrapper__handlers_circle"
                        style={{top: endPoint.y, left: endPoint.x}}
                        onMouseDown={(e) => beginDrag('end', e)}
                    />
                </div>
            )}
        </div>
    );
}

export default LineElementWrapper;
